import { match, P } from 'ts-pattern';

import { cn } from '@nesso/ui/lib/utils';
import { SidebarMenuButton, SidebarMenuItem } from '@nesso/ui/primitives/sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@nesso/ui/primitives/tooltip';

import {
    getSessionStatusIcon,
    isSessionGenerating,
    isSessionLocked,
    type SessionListItem,
} from '@/entities/session';
import { formatDurationLabel, formatTime } from '@/shared/lib/time-utils';
import { useSheetStore } from '@/shared/stores/sheet-store';

import { TodaySessionsItemGenerationSteps } from './today-sessions-item/today-sessions-item-generation-steps';

interface TodaySessionsItemProps {
    session: SessionListItem;
}

export function TodaySessionsItem({ session }: TodaySessionsItemProps) {
    const statusIcon = getSessionStatusIcon(session, { suppress: ['TODO'] });
    const date = new Date(session.date);
    const openSheet = useSheetStore((state) => state.openSheet);

    const isGenerating = isSessionGenerating(session);
    const isLocked = isSessionLocked(session);
    const activeStep =
        isLocked || isGenerating
            ? (session.currentNote?.generator?.status ?? 'PREPARING_TRANSCRIPT')
            : null;

    const handleClick = () => {
        if (isLocked) return;
        openSheet({ type: 'sessions/details', label: 'Session Note', data: session });
    };

    const itemStyle = isLocked ? undefined : statusIcon?.className;

    return (
        <>
            <SidebarMenuItem className="py-1">
                <SidebarMenuButton
                    className={cn(
                        'justify-between  px-2 h-[54px]',
                        isGenerating && 'items-start hover:cursor-not-allowed',
                        isLocked && 'hover:cursor-not-allowed',
                    )}
                    onClick={handleClick}
                >
                    <div className="flex min-w-0 flex-col text-left">
                        <span className={cn('truncate text-base', itemStyle)}>
                            {session.clients[0]?.name ?? 'Unknown client'}
                        </span>
                        <span className={cn('text-sm text-muted-foreground', itemStyle)}>
                            {formatTime(date)}, {formatDurationLabel(session.duration)}
                        </span>
                    </div>
                    {match({ activeStep, statusIcon })
                        .with(
                            {
                                activeStep: null,
                                statusIcon: P.nonNullable,
                            },
                            ({ statusIcon: { Icon, iconClassName, label } }) => (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div>
                                            <Icon className={iconClassName} />
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent side="right">{label}</TooltipContent>
                                </Tooltip>
                            ),
                        )
                        .otherwise(() => null)}
                </SidebarMenuButton>
            </SidebarMenuItem>
            {activeStep && <TodaySessionsItemGenerationSteps currentStep={activeStep} />}
        </>
    );
}
